import {
  Flex,
  Grid,
  GridItem,
  Icon,
  IconButton,
  ModalContent,
  ModalCloseButton,
  ModalFooter,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconClose } from 'assets/icons';
import Button from 'components/Button';
import FormikDateRangePicker from 'components/Formik/FormikDateRangePicker';
import { ControllableModal } from 'components/Modal';
import { FloatingFooter } from 'components/Table';
import Title from 'components/Title';
import { useFetchReportsByDateRange } from 'hooks/reports/useFetchReportsByDateRange';
// import { addMonthsToDate } from 'utils/helpers';
import { useAdpWfnStore } from 'store/useAdpWfnStore';

import ReportsByDateRangeFilterTable from './ReportsByDateRangeFilterTable';
import ReportsByDateRangeTable from './ReportsByDateRangeTable';
import { initialReportsByDateRangeFormData, reportsByDateRangeValidationSchema } from './schema';

const ReportsByDateRange: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);

  const [checkedTimesheet, setCheckedTimesheet] = useState<string[]>([]);
  const [reports, setReportsByDateRange] = useState(null);
  const [filteredReportsByDateRange, setFilteredReportsByDateRange] = useState(null);
  const [dateRange, setDateRange] = useState(initialReportsByDateRangeFormData.date_range);

  const { updateType, updateKey } = useAdpWfnStore(state => state);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { status, mutateAsync } = useFetchReportsByDateRange();

  const handleCheckboxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, timesheetId: string) => {
      if (!e.target.checked) {
        const updatedTimesheet = checkedTimesheet.filter((item: string) => item !== timesheetId);
        setCheckedTimesheet(updatedTimesheet);
      } else {
        const updatedTimesheet = [...checkedTimesheet, timesheetId];
        setCheckedTimesheet(updatedTimesheet);
      }
    },
    [checkedTimesheet]
  );

  const handleFilterButtonClick = useCallback(() => {
    const postData = {
      start_date: dateRange[0] ? format(dateRange[0], 'MM-dd-yyyy') : '',
      end_date: dateRange[1] ? format(dateRange[1], 'MM-dd-yyyy') : '',
      timesheet_ids: checkedTimesheet,
    };

    mutateAsync(postData).then(data => {
      setFilteredReportsByDateRange(data?.payload);
      onOpen();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedTimesheet, mutateAsync, dateRange]);

  const handleFormSubmit = (values: typeof initialReportsByDateRangeFormData) => {
    setDateRange(values.date_range);
    const postData = {
      start_date: values.date_range[0] ? format(values.date_range[0], 'MM-dd-yyyy') : '',
      end_date: values.date_range[1] ? format(values.date_range[1], 'MM-dd-yyyy') : '',
    };

    mutateAsync(postData).then(data => {
      updateKey(data?.payload?.attributes?.key);
      updateType(data?.payload?.attributes?.type);
      setFilteredReportsByDateRange(null);
      setCheckedTimesheet([]);
      setReportsByDateRange(data?.payload);
    });
  };

  return (
    <div>
      <Title>{t('reports:reportByDateRange')}</Title>
      <Flex mt="5" justifyContent="center">
        <Formik
          initialValues={initialReportsByDateRangeFormData}
          validationSchema={reportsByDateRangeValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {() => (
            <Form autoComplete="off">
              <Grid display="inline-grid" templateColumns={{ base: '1fr', sm: '280px 1fr' }} gap={{ base: 0, sm: 4 }}>
                <GridItem>
                  <FormikDateRangePicker label={t('common:dateRange')} name="date_range" />
                </GridItem>
                <GridItem mt={{ base: 0, sm: '6' }}>
                  <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'}>
                    {t('reports:generateReport')}
                  </Button>
                </GridItem>
              </Grid>
            </Form>
          )}
        </Formik>
      </Flex>
      {reports ? (
        <ReportsByDateRangeTable
          data={reports}
          dateRange={dateRange}
          checkedTimesheet={checkedTimesheet}
          handleCheckboxChange={handleCheckboxChange}
        >
          {checkedTimesheet.length ? (
            <FloatingFooter>
              <Flex alignItems="center" justifyContent="space-between" w="100%">
                <Flex alignItems="center">
                  <IconButton
                    aria-label="Close button"
                    icon={<Icon as={IconClose} h="10px" w="10px" />}
                    onClick={() => setCheckedTimesheet([])}
                    variant="unstyled"
                  />
                  <Text>
                    ({checkedTimesheet.length}) {t('common:selected')}
                  </Text>
                </Flex>
                <Button colorScheme="primary" onClick={handleFilterButtonClick} isLoading={status === 'loading'}>
                  {t('common:filter')}
                </Button>
              </Flex>
            </FloatingFooter>
          ) : null}
        </ReportsByDateRangeTable>
      ) : null}

      <ControllableModal size="full" isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
        <ModalContent overflowY="auto">
          <ModalCloseButton />
          <ReportsByDateRangeFilterTable data={filteredReportsByDateRange} dateRange={dateRange} />
          <ModalFooter>
            <Button onClick={onClose} w="25%">
              {t('common:close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </ControllableModal>
    </div>
  );
};

export default ReportsByDateRange;
